import { Input as BaseInput, InputProps as BaseInputProps, Tooltip, Typography } from "antd";
import {
  CSSProperties,
  ChangeEvent,
  FC,
  FocusEvent,
  HTMLAttributes,
  useEffect,
  useState,
} from "react";
import InputMask from "react-input-mask";
import { onlyNumbers } from "../../../utils/formValidation";
import { InputMode } from "../../../enums/inputTypeEnum";
import CurrencyInput from "react-currency-input-field";

const { Text } = Typography;

export type InputProps = {
  type?: string;
  inputMode?: InputMode
  label?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  innerProps?: HTMLAttributes<HTMLDivElement>;
  inputProps?: BaseInputProps;
  style?: CSSProperties;
  disabled?: boolean;
  warginig?: boolean | string;
  mask?: string;
  allowNegative?: boolean;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  min?: string,
  maxLength?: number,
  size?: string
};

const Input: FC<InputProps> = ({
  type,
  label,
  inputMode = InputMode.TEXT,
  value,
  onChange,
  placeholder,
  innerProps,
  inputProps,
  style = {},
  disabled,
  warginig,
  onKeyPress,
  allowNegative = false,
  min,
  mask,
  maxLength,
  size,
}) => {
  const isRequired = label?.endsWith("*");
  const [blur, setBlur] = useState(false);
  const [lastKeyPressed, setLastKeyPressed] = useState('')
  const title =
    label?.slice(0, label?.length - 1).toLowerCase() + " is required" ||
    "Is required";

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (e && isRequired && !value) setBlur(true);
    if (InputMode.isPhone(inputMode) && onlyNumbers(value).length === 0) setBlur(true);
  };

  useEffect(() => {
    if (InputMode.isPhone(inputMode) && onlyNumbers(value).length < 0) return setBlur(false);
    if (blur && value) setBlur(false);
  }, [value]);

  return (
    <div className="input-component">
      <div className="input-component__label">{label}</div>
      <div {...innerProps} className="input-component__input">
        <Tooltip
          zIndex={1}
          open={blur}
          title={title}
          color="orange"
          placement="bottomLeft"
          overlayClassName=""
        >
          {InputMode.isText(inputMode) && (
            <BaseInput
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type={type || "text"}
              style={style}
              disabled={disabled}
              onBlur={(e) => handleBlur(e)}
              onKeyPress={onKeyPress}
              min={min}
              maxLength={maxLength}
              {...inputProps}
            />
          )}
          {InputMode.isPassword(inputMode) && (
            <BaseInput
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              type="password"
              disabled={disabled}
              onBlur={handleBlur}
            />
          )}
          {InputMode.isPhone(inputMode) && (
            <InputMask
              className="ant-input"
              mask="(999) 999-9999"
              onBlur={handleBlur}
              value={value || ""}
              placeholder={placeholder || "(000) 000-0000"}
              onChange={(e) =>
                onChange({
                  target: {
                    value: e.target.value,
                  },
                  currentTarget: {
                    value: e.target.value,
                  },
                } as ChangeEvent<HTMLInputElement>)
              }
            />
          )}
          {InputMode.isMasked(inputMode) && (
            <InputMask
              className="ant-input"
              mask={mask || ''}
              onBlur={handleBlur}
              value={value || ""}
              alwaysShowMask={true}
              placeholder={placeholder}
              onChange={(e) =>
                onChange({
                  target: {
                    value: e.target.value,
                  },
                  currentTarget: {
                    value: e.target.value,
                  },
                } as ChangeEvent<HTMLInputElement>)
              }
            />
          )}
          {InputMode.isCurrency(inputMode) && (
            <CurrencyInput
              className="ant-input"
              onBlur={handleBlur}
              allowNegativeValue={true}
              value={value || ""}
              intlConfig={{ locale: 'en-US', currency: 'USD' }}
              prefix={"$"}
              decimalsLimit={2}
              disabled={disabled}
              placeholder={placeholder}
              onValueChange={(e) => {
                onChange({
                  target: {
                    value: e
                  },
                  currentTarget: {
                    value: e
                  }
                }as ChangeEvent<HTMLInputElement>)
              }}
            />
          )}
        </Tooltip>
      </div>
      {warginig && (
        <div className="input-component__warninng">
          <Text type="warning">{warginig}</Text>
        </div>
      )}
    </div>
  );
};

export default Input;
